import React from 'react';
import PropTypes from 'prop-types';
import { classNames, Column, Container, List, Row } from '@pella/sash-framework';
import { CTA, Image, Video } from '../..';
import { getContrastColor } from '../../utilities/functions';
import { isValidArray, objectHasValues } from '../../utilities/validations';

import './index.css';

const Feature = ({ data, className, mediaColSettings = { xs: 12, lg: 6, xl: 8 }, contentColSettings = { xs: 12, lg: 'fluid' }, ...other }) => {
	if (!data) return null;

	const bgColor = data.background_hex_value;

	const imageWrapperClasses = classNames({
		'imageBanner-imageWrapper lg:h-full': true,
		'grid h-fit overflow-hidden [&>*]:col-start-1 [&>*]:row-start-1': objectHasValues(data.highlight),
	});

	const badgeClasses = classNames({
		'imageBanner-badge text-overline w-fit': true,
		[`px-[14px] py-1 mb-2.5 ${getContrastColor(data.badge?.hex_value)}`]: data.badge?.hex_value,
		'text-neutral-600 mb-1.5': !data.badge?.hex_value,
	});

	return (
		<div
			id={data.anchor_text || null}
			className={`featureComponent ${className || ''}`}
			style={bgColor ? { backgroundColor: bgColor } : null}
			{...other}
		>
			<Container className='my-6 md:my-8 xl:my-12'>
				<Row
					reverse={data.image_position === 'right'}
					className='featureComponent-row -mx-2.5 md:-mx-4 [&>*]:px-2.5 md:[&>*]:px-4'
				>
					{isValidArray(data.image) && (
						<Column {...mediaColSettings}>
							{!data.image[0].video?.video_id && (
								<div className={imageWrapperClasses}>
									<Image
										data={data.image[0]}
										className='!block w-full lg:h-full'
									/>

									{data.highlight?.heading && (
										<div
											className='text-h1 z-2 col-start-1 row-start-1 mb-0.5 self-end leading-[44px] md:leading-[56px] xl:mb-2 xl:leading-[80px] [&>p]:inline [&>p]:bg-primary [&>p]:box-decoration-clone [&>p]:px-1 md:[&>p]:px-4 [&_sup]:-top-[.7em] [&_sup]:text-[50%]'
											dangerouslySetInnerHTML={{ __html: data.highlight.heading }}
										/>
									)}
								</div>
							)}
							{data.image?.[0]?.video?.video_id && (
								<Video
									data={data.image[0]}
									className='h-auto w-full [&>div]:h-full'
								/>
							)}
						</Column>
					)}

					<Column {...contentColSettings}>
						<div className={`featureComponent-content relative flex h-full flex-col items-start justify-center py-5 ${data.image_position === 'right' ? 'lg:pr-7' : 'lg:pl-7'}`}>
							{data.badge?.heading && (
								<p
									className={badgeClasses}
									style={data.badge.hex_value ? { backgroundColor: data.badge.hex_value } : null}
								>
									{data.badge.heading}
								</p>
							)}

							{data.heading && (
								<h2
									className={`featureComponent-contentHeading mb-3 text-28px font-bold lg:text-3xl 2xl:text-32px [&_*]:text-current [&_a]:decoration-current ${getContrastColor(bgColor)}`}
									dangerouslySetInnerHTML={{ __html: data.heading }}
								/>
							)}
							{data.description && (
								<div
									dangerouslySetInnerHTML={{ __html: data.description }}
									className={`featureComponent-contentDesc text-pretty [&_*]:text-current [&_a]:decoration-current ${getContrastColor(bgColor)}`}
								/>
							)}
							{isValidArray(data.cta) && (
								<List
									bullets='none'
									layout='inline'
									className='featureComponent-ctas'
								>
									{data.cta.map((link, index) => (
										<li
											key={index}
											className='mr-2.5 mt-2.5 max-w-full last:mr-0 last:mt-0'
										>
											<CTA
												data={link}
												className='mt-5'
												key={`cta-${index}`}
											/>
										</li>
									))}
								</List>
							)}
						</div>
					</Column>
				</Row>
			</Container>
		</div>
	);
};

Feature.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
	mediaColSettings: PropTypes.object,
	contentColSettings: PropTypes.object,
};

export default Feature;
